import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Bundler fix: Leaflet's default marker icon paths break under Vite's asset
// pipeline, so point at CDN-hosted icons instead. Runs once at module load,
// before any MapContainer/Marker renders. Shared by LocationPicker.tsx and
// SiteMap.tsx — don't duplicate this a third time, import from here.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Default view covers the hazard data's coverage: the East African Community
// (Kenya, Uganda, Tanzania, Rwanda, Burundi) plus Malawi — centred on western
// Tanzania, zoomed out enough to see from northern Kenya to southern Malawi.
export const DEFAULT_MAP_CENTER: [number, number] = [-6.0, 34.5];
export const DEFAULT_MAP_ZOOM = 4;

/**
 * Solid-fill hex equivalents of client/src/lib/level-badge.ts's
 * LEVEL_BADGE_CLASSES (same emerald/amber/red hue family — see
 * docs/design-guidelines.md "Open questions", Level-color scale). Badges use
 * pale bg + dark text; a filled map pin needs a solid, visible-on-tiles
 * color instead, so this ramps saturation (500/500/500/700) rather than
 * reusing the badge's literal Tailwind classes. Same hues, adapted for a
 * different shape — not a second color scale.
 */
export const RISK_MARKER_COLORS: Record<1 | 2 | 3 | 4, string> = {
  1: "#10b981", // emerald-500
  2: "#f59e0b", // amber-500
  3: "#ef4444", // red-500
  4: "#b91c1c", // red-700, matches the badge's Very High intensity bump
};
const PENDING_MARKER_COLOR = "#94a3b8"; // slate-400 — neutral, not yet scored

/**
 * A small colored circle marker icon, used to color map pins by risk level
 * once real risk data exists. `risk` is null while scoring is pending
 * (functional spec §6.2) — renders neutral gray, not a guessed color.
 */
export function createRiskMarkerIcon(risk: 1 | 2 | 3 | 4 | null): L.DivIcon {
  const color = risk === null ? PENDING_MARKER_COLOR : RISK_MARKER_COLORS[risk];
  return L.divIcon({
    className: "",
    html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}
